import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { AttendanceChart } from '@/components/attendance-chart';
import { useStore } from '@/lib/store-context';
import { getEmployee } from '@/lib/hr-api';
import { fmtINR, fmtDate, fmtNumber } from '@/lib/format';

export const Route = createFileRoute('/_app/hr/employees/$id')({ component: Page });

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function Page() {
  const { id } = Route.useParams();
  const { selected: store } = useStore();
  const idNum = Number(id);

  const q = useQuery({
    queryKey: ['hr:employee', store, idNum],
    queryFn: () => getEmployee({ id: idNum, store }),
    enabled: Number.isFinite(idNum),
    staleTime: 60_000,
  });

  if (q.isLoading) return <p>Loading…</p>;
  if (q.error)     return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data)     return null;
  const { employee: e, attendance, leaves, advances, advanceTotal, salaries } = q.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-2xl font-semibold">{e.name || '—'}</div>
          <div className="text-sm text-muted-foreground">
            EMPID {e.empId} · {e.empTypeLabel || 'Type ?'} ·{' '}
            {e.active
              ? <span className="text-emerald-700">Active</span>
              : <span>Discontinued{e.discontinueDate ? ` ${fmtDate(e.discontinueDate)}` : ''}</span>}
          </div>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/hr" search={(prev) => prev}>← Directory</Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Master</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Field label="Mobile"           value={e.mobile} mono />
            <Field label="Salary"           value={fmtINR(e.salary)} />
            <Field label="Commission %"     value={e.commPercent ? `${e.commPercent}%` : '—'} />
            <Field label="Morning comm"     value={e.morningComm ? fmtINR(e.morningComm) : '—'} />
            <Field label="Evening comm"     value={e.eveningComm ? fmtINR(e.eveningComm) : '—'} />
            <Field label="Tea allowance"    value={e.tea ? fmtINR(e.tea) : '—'} />
            <Field label="Leaves entitled"  value={e.leaves ? fmtNumber(e.leaves) : '—'} />
            <Field label="In / Out"         value={`${e.inTime || '—'} – ${e.outTime || '—'}`} />
            <Field label="Leave commission" value={e.leaveComm || '—'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Advance balance</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Field label="Total debit (advances given)" value={fmtINR(advanceTotal.debit)} />
            <Field label="Total credit (returned)"      value={fmtINR(advanceTotal.credit)} />
            <Field
              label="Net outstanding"
              value={fmtINR(advanceTotal.balance)}
              valueClassName={advanceTotal.balance > 0 ? 'text-amber-700 font-medium' : 'text-muted-foreground'}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Recent attendance ({attendance.length} days)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {attendance.length === 0 ? (
            <p className="text-sm text-muted-foreground">No attendance logs.</p>
          ) : (
            <>
            <AttendanceChart
              days={attendance}
              shiftStartMin={hmToMin(e.inTime)}
              shiftEndMin={hmToMin(e.outTime)}
            />
            <Table>
              <THead>
                <TR>
                  <TH>Date</TH>
                  <TH>First scan</TH>
                  <TH>Last scan</TH>
                  <TH className="text-right">Punches</TH>
                </TR>
              </THead>
              <TBody>
                {attendance.map((a) => (
                  <TR key={a.date}>
                    <TD>{fmtDate(a.date)}</TD>
                    <TD className="font-mono">{a.firstAt}</TD>
                    <TD className="font-mono">{a.lastAt}</TD>
                    <TD className="text-right">{a.entries}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Leaves</CardTitle></CardHeader>
        <CardContent>
          {leaves.length === 0 ? (
            <p className="text-sm text-muted-foreground">No leaves recorded.</p>
          ) : (
            <Table>
              <THead><TR><TH>Date</TH><TH>Type</TH></TR></THead>
              <TBody>
                {leaves.map((l) => (
                  <TR key={l.id}>
                    <TD>{fmtDate(l.date)}</TD>
                    <TD>{l.halfDay ? 'Half day' : 'Full day'}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Advance ledger (last 20)</CardTitle></CardHeader>
        <CardContent>
          {advances.length === 0 ? (
            <p className="text-sm text-muted-foreground">No advance entries.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Date</TH>
                  <TH className="text-right">Debit</TH>
                  <TH className="text-right">Credit</TH>
                  <TH>Remarks</TH>
                </TR>
              </THead>
              <TBody>
                {advances.map((a) => (
                  <TR key={a.id}>
                    <TD>{fmtDate(a.date)}</TD>
                    <TD className="text-right">{a.debit ? fmtINR(a.debit) : '—'}</TD>
                    <TD className="text-right">{a.credit ? fmtINR(a.credit) : '—'}</TD>
                    <TD>{a.remarks}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Last 6 salaries</CardTitle></CardHeader>
        <CardContent>
          {salaries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payroll history.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Period</TH>
                  <TH className="text-right">Basic</TH>
                  <TH className="text-right">Brand comm.</TH>
                  <TH className="text-right">% comm.</TH>
                  <TH className="text-right">Deductions</TH>
                  <TH className="text-right">Total paid</TH>
                </TR>
              </THead>
              <TBody>
                {salaries.map((s) => (
                  <TR key={s.id}>
                    <TD>{MONTHS[s.month] ?? s.month} {s.year}</TD>
                    <TD className="text-right">{fmtINR(s.basic)}</TD>
                    <TD className="text-right">{s.brandCommission ? fmtINR(s.brandCommission) : '—'}</TD>
                    <TD className="text-right">{s.pctCommission ? fmtINR(s.pctCommission) : '—'}</TD>
                    <TD className="text-right">{(s.lateDeduction + s.leaveDeduction) ? fmtINR(s.lateDeduction + s.leaveDeduction) : '—'}</TD>
                    <TD className="text-right font-medium">{fmtINR(s.total)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function hmToMin(s: string): number | undefined {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  return m ? Number(m[1]) * 60 + Number(m[2]) : undefined;
}

function Field({
  label, value, mono = false, valueClassName,
}: { label: string; value: string; mono?: boolean; valueClassName?: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${mono ? 'font-mono' : ''} ${valueClassName ?? ''}`.trim()}>{value || '—'}</span>
    </div>
  );
}
